import React, { useEffect, useState } from 'react';
import apiUtils, { getAuthHeaders, listAiCatalogModels } from '../utils/api';
import GenericDropdown from './GenericDropdown';
import ModelCapabilityIcons from './ModelCapabilityIcons';
import { isModelType, normalizeModelCapabilities } from '../utils/modelCapabilities';
import '../styles/ModelCapabilityIcons.css';

let modelsCache = null;
let modelsInFlight = null;

function inferProviderFromModelId(modelId) {
  const id = String(modelId || '').trim().toLowerCase();
  if (!id) return '';
  if (id.startsWith('gpt-') || /^o\d/.test(id)) return 'openai';
  if (id.startsWith('claude')) return 'anthropic';
  if (id.startsWith('gemini')) return 'google';
  if (id.startsWith('llama') || id.startsWith('mistral') || id.startsWith('qwen')) return 'ollama';
  if (id.startsWith('pansynapse')) return 'pansynapse';
  return '';
}

function matchCatalogModel(item, catalogById) {
  const candidates = catalogById.get(String(item.id || '').toLowerCase()) || [];
  if (!candidates.length) return null;

  const providerHint = String(item.provider || item.providerSlug || item.providerId || inferProviderFromModelId(item.id) || '').toLowerCase();
  if (providerHint) {
    const scoped = candidates.find((m) => {
      const providerId = String(m.providerId || '').toLowerCase();
      const apiType = String(m.providerApiType || '').toLowerCase();
      const runtime = String(m.runtimeService || '').toLowerCase();
      return providerId === providerHint || apiType === providerHint || runtime === providerHint;
    });
    if (scoped) return scoped;
  }

  return candidates[0] || null;
}

function deriveEmbeddingFlags(model = {}, catalogMatch = null) {
  const explicitType = String(model?.modelType || model?.model_type || catalogMatch?.modelType || '').trim().toLowerCase();
  const explicitEmbedding = model?.isEmbedding === true
    || model?.is_embedding === true
    || model?.catalog?.isEmbedding === true
    || catalogMatch?.isEmbedding === true;

  const id = String(model?.id || '').trim().toLowerCase();
  const family = String(model?.family || model?.catalog?.family || catalogMatch?.family || '').trim().toLowerCase();
  const inferredEmbedding = id.includes('embedding') || family.includes('embedding');

  const isEmbedding = explicitType === 'embedding'
    || explicitEmbedding
    || (explicitType !== 'llm' && inferredEmbedding);

  return {
    isEmbedding,
    modelType: isEmbedding ? 'embedding' : 'llm',
  };
}

async function fetchModelsOnce() {
  if (modelsCache && Array.isArray(modelsCache) && modelsCache.length) {
    return modelsCache;
  }
  if (modelsInFlight) return modelsInFlight;

  modelsInFlight = (async () => {
    const headers = await getAuthHeaders();
    const resp = await fetch(`${apiUtils.API_BASE_URL}/casc/v1/api/models`, {
      method: 'POST',
      headers,
      body: JSON.stringify({})
    });
    if (!resp.ok) throw new Error('Failed to load models');
    const data = await resp.json().catch(() => ({}));
    let all = [];
    if (Array.isArray(data?.models)) {
      all = data.models.map((m) => ({
        id: String(m?.id || '').trim(),
        name: String(m?.name || m?.id || '').trim(),
        provider: String(m?.providerSlug || '').trim().toLowerCase() || inferProviderFromModelId(m?.id),
        providerId: m?.providerId || null,
        providerSlug: m?.providerSlug || null,
        providerName: m?.providerName || '',
        capabilities: m?.capabilities || m?.catalog?.capabilities || null,
        modalities: m?.modalities || m?.catalog?.modalities || null,
        modelType: m?.modelType || m?.model_type || m?.catalog?.modelType || null,
        isEmbedding: m?.isEmbedding === true || m?.is_embedding === true || m?.catalog?.isEmbedding === true,
        embeddingDimensions: Number(m?.embeddingDimensions || m?.embedding_dimensions || m?.catalog?.embeddingDimensions || m?.catalog?.limits?.output || 0) || null,
        family: m?.family || m?.catalog?.family || null,
        catalog: m?.catalog || null,
      })).filter((m) => m.id);
    }
    else if (data?.models && typeof data.models === 'object') {
      Object.entries(data.models).forEach(([provider, arr]) => {
        if (!Array.isArray(arr)) return;
        arr.forEach((m) => {
          const id = String(m?.id || '').trim();
          if (!id) return;
          all.push({
            id,
            name: String(m?.name || id).trim(),
            provider: String(m?.providerSlug || provider || '').trim().toLowerCase(),
            providerId: m?.providerId || null,
            providerSlug: m?.providerSlug || String(provider || '').trim().toLowerCase(),
            providerName: m?.providerName || '',
            capabilities: m?.capabilities || m?.catalog?.capabilities || null,
            modalities: m?.modalities || m?.catalog?.modalities || null,
            modelType: m?.modelType || m?.model_type || m?.catalog?.modelType || null,
            isEmbedding: m?.isEmbedding === true || m?.is_embedding === true || m?.catalog?.isEmbedding === true,
            embeddingDimensions: Number(m?.embeddingDimensions || m?.embedding_dimensions || m?.catalog?.embeddingDimensions || m?.catalog?.limits?.output || 0) || null,
            family: m?.family || m?.catalog?.family || null,
            catalog: m?.catalog || null,
          });
        });
      });
    }

    let catalog = [];
    try {
      const catalogResult = await listAiCatalogModels({ limit: 2000 });
      catalog = Array.isArray(catalogResult?.models) ? catalogResult.models : [];
    } catch (_) {
      catalog = [];
    }

    const catalogById = new Map();
    const registerCatalogKey = (rawKey, model) => {
      const key = String(rawKey || '').trim().toLowerCase();
      if (!key) return;
      if (!catalogById.has(key)) catalogById.set(key, []);
      catalogById.get(key).push(model);
    };

    catalog.forEach((m) => {
      const idKey = String(m?.id || '').trim();
      const qualifiedKey = String(m?.qualifiedId || '').trim();
      registerCatalogKey(idKey, m);
      registerCatalogKey(qualifiedKey, m);
      if (idKey.includes('/')) registerCatalogKey(idKey.split('/').pop(), m);
      if (qualifiedKey.includes('/')) registerCatalogKey(qualifiedKey.split('/').pop(), m);
    });

    modelsCache = all
      .map((item) => {
        const catalogMatch = matchCatalogModel(item, catalogById);
        const derived = deriveEmbeddingFlags(item, catalogMatch);
        return {
          ...item,
          ...(item?.catalog && typeof item.catalog === 'object' ? { catalog: item.catalog } : {}),
          ...(catalogMatch ? {
            capabilities: item.capabilities || catalogMatch.capabilities,
            modalities: item.modalities || catalogMatch.modalities,
            isEmbedding: derived.isEmbedding,
            modelType: derived.modelType,
            providerId: item.providerId || catalogMatch.providerId,
            providerSlug: item.providerSlug || catalogMatch.providerId || item.provider,
            providerName: item.providerName || catalogMatch.providerName,
            providerApiType: catalogMatch.providerApiType,
            embeddingDimensions: item.embeddingDimensions || catalogMatch.embeddingDimensions || null,
          } : {
            capabilities: item.capabilities || { acceptsText: true, canToolCall: false },
            modalities: item.modalities || item?.catalog?.modalities,
            isEmbedding: derived.isEmbedding,
            modelType: derived.modelType,
            embeddingDimensions: item.embeddingDimensions || item?.catalog?.embeddingDimensions || item?.catalog?.limits?.output || null,
          })
        };
      })
      .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
    return modelsCache;
  })()
    .catch(() => {
      modelsCache = [];
      return modelsCache;
    })
    .finally(() => {
      modelsInFlight = null;
    });

  return modelsInFlight;
}

const ModelSelect = ({
  value = '',
  onChange,
  onModelChange,
  placeholder = 'Select a model',
  disabled = false,
  modelType = 'llm',
  providerFilter = null,
}) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchModelsOnce().then((list) => {
      if (active) {
        setModels(list || []);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (typeof onModelChange !== 'function') return;
    const model = (models || []).find((m) => m.id === value) || null;
    onModelChange(model);
  }, [models, value, onModelChange]);

function modelMatchesProvider(item, providerFilter) {
  if (!providerFilter) return true;
  const want = String(providerFilter).toLowerCase();
  const fromItem = String(item?.provider || item?.providerSlug || '').toLowerCase();
  const fromId = inferProviderFromModelId(item?.id);
  const got = fromItem || fromId;
  if (want === 'openai') return got === 'openai' || got === 'openai_compatible';
  return got === want;
}

  const filtered = Array.isArray(models)
    ? models.filter((m) => isModelType(m, modelType) && modelMatchesProvider(m, providerFilter))
    : [];

  const handleChange = (nextId) => {
    onChange && onChange(nextId);
    if (typeof onModelChange === 'function') {
      const model = filtered.find((m) => m.id === nextId) || models.find((m) => m.id === nextId) || null;
      onModelChange(model);
    }
  };

  return (
    <GenericDropdown
        items={filtered}
        value={value}
        onChange={handleChange}
        placeholder={loading ? 'Loading models...' : (filtered.length ? placeholder : 'No models available')}
        disabled={disabled || loading}
        allowNone={false}
        getItemSearchText={(item) => {
          const caps = normalizeModelCapabilities(item);
          const prov = item?.providerName || item?.provider || '';
          return `${prov} ${item?.name || item?.id || ''} ${item?.id || ''} ${caps.acceptsText ? 'text' : ''} ${caps.acceptsImages ? 'image' : ''} ${caps.acceptsPdf ? 'pdf' : ''} ${caps.acceptsVideo ? 'video' : ''} ${caps.acceptsAudio ? 'audio' : ''} ${caps.isReasoningModel ? 'reasoning' : ''} ${caps.canToolCall ? 'tool' : ''}`;
        }}
        renderValue={(item) => {
          const prov = item?.providerName || item?.provider || '';
          const provLabel = item?.providerName ? item.providerName : (prov ? `${prov.charAt(0).toUpperCase() + prov.slice(1)}` : '');
          const modelLabel = item?.name || item?.id;
          return provLabel ? `${provLabel}: ${modelLabel}` : modelLabel;
        }}
        renderOption={(item) => {
          const caps = normalizeModelCapabilities(item);
          const embeddingDimensions = Number(item?.embeddingDimensions || item?.catalog?.embeddingDimensions || item?.catalog?.limits?.output || 0) || null;
          const prov = item?.providerName || item?.provider || '';
          const provLabel = item?.providerName ? item.providerName : (prov ? `${prov.charAt(0).toUpperCase() + prov.slice(1)}` : '');
          const modelLabel = item?.name || item?.id;
          const displayName = provLabel ? `${provLabel}: ${modelLabel}` : modelLabel;
          return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{displayName}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {caps.isEmbedding && (
                  <span className="model-embedding-dimensions" title={embeddingDimensions ? `${embeddingDimensions} dimensions` : 'Embedding model'}>
                    <i className="fas fa-vector-square" aria-hidden="true" />
                    <span>{embeddingDimensions ? `${embeddingDimensions}d` : 'Embedding'}</span>
                  </span>
                )}
                <ModelCapabilityIcons model={item} />
              </span>
            </span>
          );
      }}
    />
  );
};

export default ModelSelect;
